import { EmptyState } from '@/components/common/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { SLIM_SCROLLBAR_CLASS } from '@/styles/scrollbar';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatDisplayDate, formatWeekday } from '@/utils/date';
import {
  LEAVE_REPORT_DATE_CATEGORY,
  LEAVE_REPORT_DAY_KIND,
} from '../constants/leaveReport.constants';
import type {
  LeaveReportDateCategory,
  LeaveReportDates,
  LeaveReportDayKind,
} from '../types/leaveReport.types';
import {
  getDayKindByDate,
  getWeekdayLabels,
  type CalendarDay,
  type CalendarMonth,
} from '../utils/leaveReportCalendar';

const WEEKDAY_LABELS = getWeekdayLabels();

const MONTH_LIST_CLASS = '-m-1 grid max-h-[55dvh] gap-3 overflow-y-auto p-1';
const SIDE_BY_SIDE_MONTH_LIST_CLASS = 'sm:grid-cols-2';
const MONTH_CARD_CLASS = 'flex flex-col gap-2 rounded-xl bg-card p-3 ring-1 ring-border';
const WEEK_GRID_CLASS = 'grid grid-cols-7 gap-1';
const DAY_CELL_CLASS = 'flex h-9 items-center justify-center rounded-lg text-sm tabular-nums';
const LEGEND_SWATCH_CLASS = 'size-3 shrink-0 rounded-sm ring-1 ring-foreground/10 ring-inset';

interface LeaveReportDatesCalendarProps {
  dates: LeaveReportDates | undefined;
  category: LeaveReportDateCategory;
  months: CalendarMonth[];
  isSideBySide: boolean;
}

export function LeaveReportDatesCalendar({
  dates,
  category,
  months,
  isSideBySide,
}: LeaveReportDatesCalendarProps) {
  const { dayKinds } = LEAVE_REPORT_DATE_CATEGORY[category];

  if (months.length === 0) {
    return (
      <EmptyState
        size="compact"
        title="No dates found"
        description="There is no date to show for this period."
      />
    );
  }

  const dayKindByDate = dates ? getDayKindByDate(dates, dayKinds) : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          MONTH_LIST_CLASS,
          isSideBySide && SIDE_BY_SIDE_MONTH_LIST_CLASS,
          SLIM_SCROLLBAR_CLASS,
        )}
      >
        {months.map((month) => (
          <CalendarMonthCard key={month.key} month={month} dayKindByDate={dayKindByDate} />
        ))}
      </div>
      <DayKindLegend dayKinds={dayKinds} />
    </div>
  );
}

interface CalendarMonthCardProps {
  month: CalendarMonth;
  dayKindByDate: Map<string, LeaveReportDayKind> | undefined;
}

function CalendarMonthCard({ month, dayKindByDate }: CalendarMonthCardProps) {
  return (
    <section className={MONTH_CARD_CLASS} aria-label={month.label}>
      <h3 className="text-sm font-semibold">{month.label}</h3>
      <WeekdayHeader />
      <ol className={WEEK_GRID_CLASS}>
        {month.days.map((day) => (
          <CalendarDayCell
            key={day.isoDate}
            day={day}
            isLoading={dayKindByDate === undefined}
            dayKind={dayKindByDate?.get(day.isoDate)}
          />
        ))}
      </ol>
    </section>
  );
}

function WeekdayHeader() {
  return (
    <div className={WEEK_GRID_CLASS} aria-hidden>
      {WEEKDAY_LABELS.map((weekday) => (
        <span key={weekday} className="text-center text-2xs font-medium text-muted-foreground">
          {weekday}
        </span>
      ))}
    </div>
  );
}

interface CalendarDayCellProps {
  day: CalendarDay;
  isLoading: boolean;
  dayKind: LeaveReportDayKind | undefined;
}

function CalendarDayCell({ day, isLoading, dayKind }: CalendarDayCellProps) {
  if (!day.isInMonth) return <li aria-hidden />;

  if (isLoading) {
    return (
      <li aria-hidden>
        <Skeleton className="h-9 rounded-lg" />
      </li>
    );
  }

  if (!dayKind) {
    return (
      <li
        aria-hidden
        className={cn(
          DAY_CELL_CLASS,
          day.isInPeriod ? 'text-foreground' : 'text-muted-foreground/50',
        )}
      >
        {day.dayOfMonth}
      </li>
    );
  }

  const { label, tone } = LEAVE_REPORT_DAY_KIND[dayKind];
  return (
    <li className={cn(DAY_CELL_CLASS, STATUS_COLOR_CLASS[tone], 'font-semibold')}>
      <span aria-hidden>{day.dayOfMonth}</span>
      <span className="sr-only">
        {`${formatWeekday(day.isoDate)}, ${formatDisplayDate(day.isoDate)}: ${label}`}
      </span>
    </li>
  );
}

interface DayKindLegendProps {
  dayKinds: readonly LeaveReportDayKind[];
}

function DayKindLegend({ dayKinds }: DayKindLegendProps) {
  return (
    <ul
      className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground"
      aria-hidden
    >
      {dayKinds.map((dayKind) => {
        const { label, tone } = LEAVE_REPORT_DAY_KIND[dayKind];
        return (
          <li key={dayKind} className="flex items-center gap-1.5">
            <span className={cn(LEGEND_SWATCH_CLASS, STATUS_COLOR_CLASS[tone])} />
            {label}
          </li>
        );
      })}
    </ul>
  );
}
